<template>
  <v2-modal
    :show="show"
    title="Edit retailer"
    size="md"
    @close="$emit('close')"
  >
    <div
      v-if="localUserData"
      class="v2-edit-user"
    >
      <form @submit.prevent="updateWalletRule">
        <div class="v2-flex v2-flex-col v2-gap-4">
          <!-- Payment Terms -->
          <div class="v2-input-wrapper">
            <label class="v2-input__label">Payment Terms</label>
            <v-select
              v-model="localUserData.paymentTerms"
              :options="paymentTermOptions"
              placeholder="Payment Terms"
              class="v2-v-select"
            />
          </div>

          <!-- Logistics Terms -->
          <div class="v2-input-wrapper">
            <label class="v2-input__label">Logistics Terms</label>
            <v-select
              v-model="localUserData.logisticsTerms"
              :options="logisticsTermOptions"
              placeholder="Logistics Terms"
              class="v2-v-select"
            />
          </div>

          <!-- Price Books -->
          <div class="v2-input-wrapper">
            <label class="v2-input__label">Price Books</label>
            <v-select
              v-model="selectedPriceBooks"
              :options="flatPriceBookOptions"
              multiple
              :close-on-select="false"
              label="priceBookName"
              placeholder="Select price books"
              class="v2-v-select-multi"
              :reduce="book => book"
              :selectable="option => !option.$isGroup"
            >
              <template #option="option">
                <div
                  v-if="option.$isGroup"
                  class="v2-edit-user__group-label"
                >
                  {{ option.priceBookName }}
                </div>
                <span v-else>{{ option.priceBookName }}</span>
              </template>
              <template #selected-option="option">
                {{ option.priceBookName }}
              </template>
            </v-select>
          </div>

          <!-- Segmentation Books (if feature enabled) -->
          <div
            v-if="hasTransactionCurrencyFeature"
            class="v2-input-wrapper"
          >
            <label class="v2-input__label">Segmentation Books</label>
            <v-select
              v-model="selectedSegmentationBooks"
              :options="flatSegmentationBookOptions"
              multiple
              :close-on-select="false"
              label="priceBookName"
              placeholder="Select segmentation books"
              class="v2-v-select-multi"
              :reduce="book => book"
              :selectable="option => !option.$isGroup"
            >
              <template #option="option">
                <div
                  v-if="option.$isGroup"
                  class="v2-edit-user__group-label"
                >
                  {{ option.priceBookName }}
                </div>
                <span v-else>{{ option.priceBookName }}</span>
              </template>
              <template #selected-option="option">
                {{ option.priceBookName }}
              </template>
            </v-select>
          </div>

          <!-- Currency -->
          <div class="v2-input-wrapper">
            <label class="v2-input__label">Currency</label>
            <v-select
              :value="localUserData.availableCurrencies || []"
              :options="availableCurrencies"
              multiple
              :close-on-select="false"
              placeholder="Select currencies"
              class="v2-v-select-multi"
              @input="onCurrenciesChange"
            />
          </div>

          <!-- Transaction Currency (if feature enabled) -->
          <div
            v-if="hasTransactionCurrencyFeature"
            class="v2-input-wrapper"
          >
            <label class="v2-input__label">Transaction Currency</label>
            <v-select
              v-model="localUserData.transactionCurrency"
              :options="availableCurrencies"
              placeholder="Select currency"
              class="v2-v-select"
            />
          </div>

          <!-- Discount -->
          <v2-input
            v-model="discount"
            label="Discount(%)"
            placeholder="Discount"
            type="number"
            step="any"
            :error="discountError"
          />

          <!-- Discount preview -->
          <div class="v2-text-sm v2-text-muted">
            <div>Ex: If your wholesale price is</div>
            <div class="v2-flex v2-items-center v2-gap-2">
              <input
                v-model="exampleWholeSalePrice"
                type="number"
                class="v2-edit-user__example-price"
                :disabled="!!discountError"
              >
              <span>, then the updated price</span>
            </div>
            <div>
              for this retailer is
              <span class="v2-font-semibold">{{ getUpdatedPriceForRetailer }}</span>
            </div>
          </div>
        </div>

        <div class="v2-flex v2-justify-end v2-gap-2 v2-mt-6">
          <v2-button
            variant="outline"
            @click="$emit('close')"
          >
            Cancel
          </v2-button>
          <v2-button
            type="submit"
            :disabled="!!discountError || isEditing"
            :loading="isEditing"
          >
            Save
          </v2-button>
        </div>
      </form>
    </div>

    <!-- Confirm Currency Change Modal -->
    <v2-modal
      :show="showCurrencyConfirm"
      title="Confirm Currency Change"
      size="sm"
      @close="cancelCurrencyChange"
    >
      <p class="v2-text-sm v2-text-muted">
        Changing the transaction currency will remove all linked price books. Previous order values will remain unchanged. Are you sure you want to continue?
      </p>
      <div class="v2-flex v2-justify-end v2-gap-2 v2-mt-6">
        <v2-button
          variant="outline"
          @click="cancelCurrencyChange"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="destructive"
          @click="confirmCurrencyChange"
        >
          Yes, Change Currency
        </v2-button>
      </div>
    </v2-modal>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Button from '@/components/v2/V2Button.vue'
import vSelect from 'vue-select'
import { useEditUser } from '@/composables/useEditUser'

export default {
  name: 'V2EditUserModal',
  components: {
    V2Modal,
    V2Input,
    V2Button,
    vSelect,
  },
  props: {
    show: { type: Boolean, default: false },
    userDetails: { type: Object, default: () => null },
    loadConnections: { type: Function, default: () => {} },
  },
  setup() {
    return { ...useEditUser() }
  },
  data() {
    return {
      showCurrencyConfirm: false,
    }
  },
  computed: {
    flatPriceBookOptions() {
      return this.flattenBookOptions(
        this.collectionWithPriceBooks,
        name => `Collection: ${name || ''}`,
      )
    },
    flatSegmentationBookOptions() {
      return this.flattenBookOptions(
        this.collectionWithSegmentationBooks,
        name => name || '',
      )
    },
  },
  watch: {
    show(val) {
      if (val && this.userDetails) {
        this.initEditUser(this.userDetails)
      }
    },
  },
  methods: {
    flattenBookOptions(collections, formatLabel) {
      const options = []
      for (const collection of collections) {
        if (collection.priceBooks.length) {
          options.push({
            priceBookName: formatLabel(collection.collectionName),
            $isGroup: true,
            $isDisabled: true,
          })
          for (const book of collection.priceBooks) {
            options.push(book)
          }
        }
      }
      return options
    },
    onCurrenciesChange(currencies) {
      this.localUserData.availableCurrencies = currencies || []
    },
    async updateWalletRule() {
      const { currencyChanged } = this.buildUpdatePayload()
      if (currencyChanged) {
        this.showCurrencyConfirm = true
        return
      }
      await this.saveUpdate()
    },
    cancelCurrencyChange() {
      this.showCurrencyConfirm = false
    },
    async confirmCurrencyChange() {
      this.showCurrencyConfirm = false
      await this.saveUpdate()
    },
    async saveUpdate() {
      const { success } = await this.submitUpdate()
      if (success) {
        await this.loadConnections(true)
        this.$emit('close')
      }
    },
  },
}
</script>

<style lang="scss">
// vue-select V2 theme for edit modal
@mixin v2-vselect-base {
  .vs__dropdown-toggle {
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-md);
    background: var(--v2-background);
    min-height: 2.714rem;
    transition: border-color 0.15s ease;
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: var(--v2-primary);
  }

  .vs__selected-options {
    padding: 0;
  }

  .vs__search {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    margin: 0;

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  .vs__actions {
    padding: 0 8px 0 4px;
  }

  .vs__dropdown-menu {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-shadow-md);
    padding: var(--v2-space-1) 0;
  }

  .vs__dropdown-option {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    padding: var(--v2-space-2) var(--v2-space-3);
    color: var(--v2-foreground);

    &--highlight {
      background: var(--v2-muted);
      color: var(--v2-foreground);
    }

    &--disabled {
      opacity: 1 !important;
      cursor: default;
    }
  }

  .vs__clear,
  .vs__open-indicator {
    fill: var(--v2-muted-foreground);
  }
}

.v2-edit-user {
  .v2-v-select {
    &.v-select {
      @include v2-vselect-base;

      .vs__dropdown-toggle {
        padding: 0;
      }

      .vs__selected-options {
        flex-wrap: nowrap;
      }

      .vs__search {
        padding: 6px 12px;
        line-height: 1.5;
      }

      .vs__selected {
        font-family: var(--v2-font-sans);
        font-size: 0.875rem;
        color: var(--v2-foreground);
        margin: 0;
        padding: 6px 12px;
        line-height: 1.5;
      }

      &.vs--open .vs__selected {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .vs__dropdown-menu {
        max-height: 200px;
      }

      &.vs--disabled .vs__dropdown-toggle {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: var(--v2-muted);
      }
    }
  }

  .v2-v-select-multi {
    &.v-select {
      @include v2-vselect-base;

      .vs__dropdown-toggle {
        padding: 4px 0 4px 8px;
      }

      .vs__selected-options {
        flex-wrap: wrap;
        gap: 4px;
      }

      .vs__search {
        padding: 2px 4px;
      }

      .vs__selected {
        font-family: var(--v2-font-sans);
        font-size: 0.75rem;
        color: var(--v2-primary);
        background: rgba(116, 125, 238, 0.1);
        border: 1px solid rgba(116, 125, 238, 0.2);
        border-radius: 4px;
        margin: 0;
        padding: 2px 6px;
      }

      .vs__deselect {
        fill: var(--v2-primary);
        margin-left: 4px;
      }

      .vs__dropdown-menu {
        max-height: 250px;
      }

      .vs__dropdown-option {
        &--selected {
          background: rgba(116, 125, 238, 0.05);
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.v2-edit-user {
  &__group-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--v2-primary);
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
    padding: 2px var(--v2-space-2);
    font-size: 0.75rem;
    background: var(--v2-muted);
    color: var(--v2-primary);
    border-radius: var(--v2-radius-sm);
  }

  &__tag-remove {
    background: none;
    border: none;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    color: var(--v2-muted-foreground);
    padding: 0;

    &:hover {
      color: var(--v2-destructive);
    }
  }

  &__example-price {
    width: 60px;
    padding: var(--v2-space-1);
    border: none;
    border-bottom: 1px solid var(--v2-foreground);
    border-radius: 0;
    font-size: 0.875rem;
    outline: none;
    background: transparent;
    color: var(--v2-foreground);

    &:focus {
      border-bottom-color: var(--v2-primary);
    }

    &:disabled {
      opacity: 0.5;
    }
  }
}

.v2-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-1);
}

.v2-input__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v2-foreground);
}
</style>
